// Words, colours and icons of the Activity view — one place, so every list,
// chart and drawer says the same thing about an event.
import {
  faComments, faHeadphones, faMagnifyingGlassChart, faFileLines, faPeopleGroup, faRightToBracket,
} from '@fortawesome/free-solid-svg-icons';

export const CATEGORIES = [
  { key: 'chat', label: 'Chats', icon: faComments },
  { key: 'audio', label: 'Audio', icon: faHeadphones },
  { key: 'analysis', label: 'Analyses', icon: faMagnifyingGlassChart },
  { key: 'document', label: 'Documents', icon: faFileLines },
  { key: 'meeting', label: 'Meetings & notes', icon: faPeopleGroup },
  { key: 'access', label: 'Logins & navigation', icon: faRightToBracket },
];
export const CATEGORY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

export const PERIODS = [
  { hours: 24, label: '24 h', long: 'last 24 hours' },
  { hours: 168, label: '7 days', long: 'last 7 days' },
  { hours: 720, label: '30 days', long: 'last 30 days' },
  { hours: 2160, label: '90 days', long: 'last 90 days' },
  { hours: 8760, label: '12 months', long: 'last 12 months' },
];

const AREAS = {
  efd: 'Bro EFD', ppi: 'Bro PPI', assistant: 'Assistant Bro', are: 'Advisor Reasoning', are_free: 'ARE free chat',
  insurance: 'HeyBro Insurance', cards: 'Audio briefings', meeting: 'Meetings', notes: 'Client notes',
  screener: 'theScreener', files: 'Files', app: 'App', console: 'Admin console', chat: 'Chat',
};
export const areaLabel = (a) => AREAS[a] || a || '—';

const MODULES = {
  chat: 'Assistant Bro', client_notes: 'Client notes', meeting_minutes: 'Meeting minutes',
  advisor_reasoning: 'Advisor Reasoning', efd_agent: 'Bro EFD', heybro_insurance: 'HeyBro Insurance', ppi_agent: 'Bro PPI',
};

// Own audio cards (src/audio/) — what the user did with one.
const AUDIO_CARD_ACTIONS = { created: 'created', changed: 'changed', deleted: 'deleted', restored: 'restored' };

export function cardLabel(type) {
  const t = String(type || '');
  if (t.startsWith('asset_under_mgt')) return 'Assets under management';
  if (t.startsWith('net_new_asset')) return 'Net new assets';
  if (t.startsWith('new_clients')) return 'New clients';
  if (t.startsWith('income')) return 'Income';
  if (t.startsWith('contacted_clients')) return 'Contacted clients';
  if (t.startsWith('kpi')) return 'KPIs';
  return t ? t.replace(/_/g, ' ') : 'briefing';
}

const DOC_TYPES = {
  powerpoint: 'PowerPoint', pdf: 'PDF', word: 'Word document', document: 'document', email: 'e-mail draft',
  chart: 'chart', report: 'client report',
};
export const docLabel = (t) => DOC_TYPES[t] || t || 'document';
export const docTitle = (t) => { const s = docLabel(t); return s.charAt(0).toUpperCase() + s.slice(1); };

const LOGIN_REASONS = { wrong_password: 'wrong password', unknown_user: 'unknown user', deactivated: 'account deactivated' };

/** One sentence for an event: { verb, object } — "asked Bro EFD" · "Wie hoch …". */
export function describe(e) {
  const area = areaLabel(e.area);
  switch (e.kind) {
    case 'chat':
      return { verb: e.channel === 'voice' ? `asked ${area} by voice` : `asked ${area}`, object: e.title };
    case 'assistant_open':
      return { verb: `opened ${area}`, object: e.data_set ? `data: ${e.data_set}` : '' };
    case 'module_open':
      return { verb: `opened ${MODULES[e.title] || e.title}`, object: '' };
    case 'login':
      if (e.status === 'error') return { verb: 'failed to log in', object: LOGIN_REASONS[e.reason] || e.reason || '' };
      return { verb: e.area === 'console' ? 'logged in (admin console)' : 'logged in', object: '' };
    case 'logout':
      return { verb: 'logged out', object: '' };
    case 'audio_open':
      return { verb: `opened the ${cardLabel(e.title)} briefing`, object: '' };
    case 'audio_play':
      return { verb: `listened to ${cardLabel(e.title)}`, object: e.seconds ? clock(Number(e.seconds)) : '' };
    case 'audio_card':
      return { verb: `${AUDIO_CARD_ACTIONS[e.action] || 'changed'} an own audio${e.voice ? ' by voice' : ''}`, object: e.title };
    case 'analysis':
      return { verb: 'started an analysis', object: e.title };
    case 'analysis_action':
      return { verb: 'worked on an analysis', object: e.title };
    case 'dossier':
      return { verb: 'ordered a company dossier', object: e.title };
    case 'document':
      return { verb: `created a ${docLabel(e.doc_type)}`, object: e.title };
    case 'chart_saved':
      return { verb: 'starred a chart', object: e.title };
    case 'file_open':
      return { verb: 'opened a file', object: e.title };
    case 'email_open':
      return { verb: 'opened an e-mail draft', object: e.title };
    case 'meeting':
      return { verb: 'recorded a meeting', object: e.title };
    case 'note':
      return { verb: 'added a client note', object: e.title };
    default:
      return { verb: e.kind, object: e.title };
  }
}

// ── people ───────────────────────────────────────────────────────────────────
export const fullName = (u) => [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim();
export const initials = (u) => {
  const n = fullName(u);
  const src = n || u?.username || '?';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  return ((parts[0]?.[0] || '') + (parts[1]?.[0] || parts[0]?.[1] || '')).toUpperCase();
};
export const hue = (s) => {
  let h = 0;
  for (const ch of String(s || '')) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
};

// ── time & numbers ───────────────────────────────────────────────────────────
export const toDate = (iso) => (iso ? new Date(iso) : null);

export function ago(iso, now = Date.now()) {
  const d = toDate(iso);
  if (!d) return 'never';
  const s = Math.max(0, (now - d.getTime()) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 2 * 86400) return `yesterday ${d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })}`;
  if (s < 7 * 86400) return `${Math.round(s / 86400)} days ago`;
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export const timeOf = (iso) => toDate(iso)?.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' }) || '';
export const dateTime = (iso) => toDate(iso)?.toLocaleString('de-CH', {
  weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) || '—';

export function dayLabel(iso) {
  const d = toDate(iso);
  if (!d) return '';
  const today = new Date();
  const k = (x) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  if (k(d) === k(today)) return 'Today';
  if (k(d) === k(y)) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}
export const dayKey = (iso) => {
  const d = toDate(iso);
  return d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
};

export function clock(seconds) {
  if (!Number.isFinite(seconds)) return '';
  const s = Math.round(seconds);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function duration(ms) {
  if (ms == null) return '—';
  const s = ms / 1000;
  if (s < 1) return `${Math.round(ms)} ms`;
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ${Math.round(s % 60)} s`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

export const num = (n) => (n == null ? '—' : Number(n).toLocaleString('de-CH'));
export function compact(n) {
  if (n == null) return '—';
  const a = Math.abs(n);
  if (a >= 1e6) return `${(n / 1e6).toFixed(a >= 1e7 ? 0 : 1)}M`;
  if (a >= 1e4) return `${(n / 1e3).toFixed(0)}k`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(n);
}

/** Change against the previous period, as { text, tone } (or null). */
export function delta(now, before) {
  if (now == null || before == null) return null;
  if (!before && !now) return null;
  if (!before) return { text: 'new', tone: 'up' };
  const pct = ((now - before) / before) * 100;
  if (Math.abs(pct) < 0.5) return { text: '±0 %', tone: 'flat' };
  return { text: `${pct > 0 ? '+' : ''}${Math.round(pct)} %`, tone: pct > 0 ? 'up' : 'down' };
}

/** The bucket keys of a window, oldest first: local days ('YYYY-MM-DD') or hours ('YYYY-MM-DDTHH'). */
export function buckets(hours, now = new Date()) {
  const out = [];
  if (hours <= 48) {
    const d = new Date(now);
    d.setMinutes(0, 0, 0);
    for (let i = hours - 1; i >= 0; i -= 1) {
      const x = new Date(d.getTime() - i * 3600e3);
      out.push(`${dayKey(x.toISOString())}T${String(x.getHours()).padStart(2, '0')}`);
    }
    return out;
  }
  const days = Math.round(hours / 24);
  for (let i = days - 1; i >= 0; i -= 1) {
    const x = new Date(now);
    x.setDate(x.getDate() - i);
    out.push(dayKey(x.toISOString()));
  }
  return out;
}

/** series rows -> [{ key, total, chat, audio, … }] over every bucket of the window. */
export function seriesByBucket(series, hours) {
  const keys = buckets(hours);
  const hourly = hours <= 48;
  const map = Object.fromEntries(keys.map((k) => [k, { key: k, total: 0 }]));
  (series || []).forEach((r) => {
    const k = hourly ? r.bucket.slice(0, 13) : r.bucket.slice(0, 10);
    const b = map[k];
    if (!b) return;
    b[r.category] = (b[r.category] || 0) + r.n;
    b.total += r.n;
  });
  return keys.map((k) => map[k]);
}

export function bucketLabel(key, hourly) {
  if (hourly) return `${key.slice(11, 13)}:00`;
  const d = new Date(`${key}T12:00:00`);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
